import { ClientOnly } from "@tanstack/react-router";
import { format } from "date-fns/format";

import { formatDistance } from "date-fns/formatDistance";
import { ClipboardCopyIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import type { ReviewRequest } from "@/lib/client/review";
import { cn } from "@/lib/utils";
import { truncatedEmail } from "@/lib/utils/truncate";
import { ReviewRequestStatusBadge } from "@/features/review-request/components/review-request-status-badge";
import { ReviewRequestTypeBadge } from "@/features/review-request/components/review-request-type-badge";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { AdminActions } from "./admin-actions";
import { AiOptimizeDescription } from "./ai";
import { UserActions } from "./user-actions";

export function ReviewRequestItem({
  request,
  email,
  name,
  userActions,
  adminActions,
  queuePosition,
  displayTime,
  clampDescription,
  className,
  onModified,
}: {
  request: ReviewRequest;
  clampDescription?: boolean;
  sort?: number;
  name: string;
  email: string | null;
  userActions?: boolean;
  adminActions?: boolean;
  queuePosition?: number | null;
  displayTime?: Date;
  className?: string;
  onModified?: () => void;
}) {
  const timeToDisplay = displayTime ?? request.createdAt;

  return (
    <article className={className}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <div className="flex-1 w-1 gap-x-1 flex flex-nowrap overflow-auto py-1">
              <ReviewRequestStatusBadge className="mr-2" status={request.status} />

              <ReviewRequestTypeBadge className="mr-2" type={request.type} />

              {request.priorityQueue > 0 && (
                <Badge variant="destructive" className="font-bold ring-1 ring-foreground">
                  舰长优先队列
                </Badge>
              )}

              {request.status === "queued" && typeof queuePosition === "number" && (
                <Badge variant="outline" className="font-medium">
                  排队第 {queuePosition} 位
                </Badge>
              )}
            </div>

            <h1 className="min-w-0 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <span className="line-clamp-1 overflow-hidden text-ellipsis">
                  {email && name !== email
                    ? `${name} (${truncatedEmail(email)})`
                    : email
                      ? truncatedEmail(email)
                      : name}
                </span>
                {email && (
                  <ClipboardCopyIcon
                    onClick={() => {
                      window.navigator.clipboard.writeText(email);
                      toast.success("已复制");
                    }}
                    className="shrink-0 cursor-pointer size-5"
                  />
                )}
              </span>
            </h1>
          </CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col space-y-3">
          <Section label="复盘描述">
            <p
              className={cn(
                "text-balance tracking-wide leading-relaxed break-words whitespace-pre-wrap overflow-hidden text-ellipsis",
                clampDescription && "line-clamp-4",
              )}
            >
              {request.description}
            </p>

            <AiOptimizeDescription
              id={request.id}
              onSuccess={() => {
                onModified?.();
              }}
            />
          </Section>

          {request.aiOptimizedDescription && (
            <Section label="复盘描述 (AI)">
              <p
                className={cn(
                  "text-balance tracking-wide leading-relaxed break-words whitespace-pre-wrap overflow-hidden text-ellipsis",
                  clampDescription && "line-clamp-4",
                )}
              >
                {request.aiOptimizedDescription}
              </p>
            </Section>
          )}

          <Section label="相关视频链接">
            <ul>
              {request.videoLinks.map(({ url }) => (
                <li key={url} className="flex items-center space-x-2">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 visited:text-blue-800 w-fit text-sm line-clamp-1 overflow-hidden text-ellipsis"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </Section>

          {request.status === "reviewed" && (
            <Section label="复盘反馈">
              {request.feedback ? (
                <Dialog>
                  <div className="flex items-center justify-between gap-2">
                    <p className="line-clamp-1">{request.feedback.substring(0, 50)}...</p>
                    <DialogTrigger
                      nativeButton={true}
                      render={
                        <Button
                          variant="link"
                          className="m-0 p-0 h-auto inline text-blue-400 shrink-0"
                        >
                          查看全文
                        </Button>
                      }
                    />
                  </div>
                  <DialogContent>
                    <p className="text-balance tracking-wide leading-relaxed whitespace-pre-wrap">
                      {request.feedback}
                    </p>
                  </DialogContent>
                </Dialog>
              ) : (
                <p className="text-gray-400 text-sm">复盘已结束，但是没有给出反馈内容。</p>
              )}
            </Section>
          )}

          {request.status === "rejected" && (
            <Section label="复盘请求已拒绝，理由：">
              {request.rejectReason ? (
                <Dialog>
                  <div className="flex items-center justify-between gap-2">
                    <p className="line-clamp-1">{request.rejectReason.substring(0, 50)}...</p>
                    <DialogTrigger
                      nativeButton={true}
                      render={
                        <Button
                          variant="link"
                          className="m-0 p-0 h-auto inline text-blue-400 shrink-0"
                        >
                          查看全文
                        </Button>
                      }
                    />
                  </div>
                  <DialogContent>
                    <p className="text-balance text-red-600 tracking-wide leading-relaxed whitespace-pre-wrap">
                      {request.rejectReason}
                    </p>
                  </DialogContent>
                </Dialog>
              ) : (
                <p className="text-gray-400 text-sm">复盘已被拒绝，但是没有给出拒绝原因。</p>
              )}
            </Section>
          )}
        </CardContent>

        <CardFooter className="flex items-center space-x-2 justify-between">
          <ClientOnly fallback={<p className="text-[10px] text-gray-500">...</p>}>
            <div className="inline-grid shrink-0 place-items-start gap-y-1">
              <p className="text-[10px] text-gray-500">{format(timeToDisplay, "PPPp")}</p>
              <p className="text-xs text-gray-400">
                {formatDistance(new Date(), timeToDisplay, { addSuffix: true })}
              </p>
            </div>
          </ClientOnly>

          {adminActions && (
            <div className="flex items-center space-x-2">
              {(request.status === "queued" || request.status === "reviewing") && (
                <AdminActions.ReviewRejectionButton id={request.id} />
              )}

              {request.status === "queued" && (
                <AdminActions.ReviewConfirmationButton id={request.id} />
              )}

              {request.status === "reviewing" && (
                <AdminActions.ReviewCompletionButton id={request.id} />
              )}
            </div>
          )}

          {userActions && (
            <div className="flex items-center space-x-2">
              {request.status === "queued" && (
                <UserActions.CancelReviewButton id={request.id} onSuccess={onModified} />
              )}
              {(request.status === "cancelled" || request.status === "rejected") && (
                <UserActions.RestorateReviewButton id={request.id} onSuccess={onModified} />
              )}
              {(request.status === "rejected" ||
                request.status === "cancelled" ||
                request.status === "queued") && (
                <UserActions.UpdateReviewButton id={request.id} onSuccess={onModified} />
              )}
            </div>
          )}
        </CardFooter>
      </Card>
    </article>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <h2 className="text-gray-500 text-sm mb-1">{label}</h2>
      {children}
    </div>
  );
}
